import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import AdBanner from '@/components/AdBanner';
import { toast } from 'sonner';
import {
  MapPin, Clock, Eye, Star, MessageSquare, User, ArrowLeft, Flag, ShoppingBag, Gift, Repeat, Loader2, ChevronLeft, ChevronRight
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const categoryStyles = {
  sale: { label: 'For Sale', icon: ShoppingBag, className: 'bg-[#F4F1EA] text-[#2C362F]' },
  free: { label: 'Free', icon: Gift, className: 'bg-[#E5B66A]/10 text-[#2C362F]' },
  lending: { label: 'Lending', icon: Repeat, className: 'bg-[#7D9B8B]/10 text-[#7D9B8B]' },
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    api.get(`/listings/${id}`).then(r => setListing(r.data)).catch(() => navigate('/browse')).finally(() => setLoading(false));
  }, [id, navigate]);

  const handleContact = async () => {
    if (!user || !user.user_id) { navigate('/login'); return; }
    if (!message.trim()) { toast.error('Please enter a message'); return; }
    setSending(true);
    try {
      await api.post('/conversations', {
        recipient_id: listing.user_id,
        listing_id: listing.listing_id,
        initial_message: message,
      });
      toast.success('Message sent!');
      setMessage('');
      navigate('/messages');
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReport = async () => {
    if (!user || !user.user_id) { navigate('/login'); return; }
    try {
      await api.post('/reports', {
        target_type: 'listing',
        target_id: listing.listing_id,
        reason: 'Inappropriate content',
      });
      toast.success('Report submitted');
    } catch {
      toast.error('Failed to report');
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
  );
  if (!listing) return null;

  const cat = categoryStyles[listing.category] || categoryStyles.sale;
  const images = listing.images && listing.images.length > 0
    ? listing.images.map(img => `${API_BASE}/api/files/${img}`)
    : ['https://images.unsplash.com/photo-1578653282816-a508bd2d0435?w=800&h=600&fit=crop'];

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days < 1) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen" data-testid="listing-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6">
        <Button variant="ghost" className="rounded-full mb-4 gap-1.5" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Images */}
          <div className="lg:col-span-2">
            <div className="relative rounded-2xl overflow-hidden bg-muted aspect-[4/3]">
              <img src={images[imgIdx]} alt={listing.title} className="w-full h-full object-cover" data-testid="listing-main-image" />
              {images.length > 1 && (
                <>
                  <Button
                    variant="ghost" size="icon"
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white"
                    onClick={() => setImgIdx(i => (i - 1 + images.length) % images.length)}
                    data-testid="img-prev-btn"
                  ><ChevronLeft className="w-5 h-5" /></Button>
                  <Button
                    variant="ghost" size="icon"
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white"
                    onClick={() => setImgIdx(i => (i + 1) % images.length)}
                    data-testid="img-next-btn"
                  ><ChevronRight className="w-5 h-5" /></Button>
                </>
              )}
              <Badge className={`absolute top-4 left-4 rounded-full text-xs font-semibold px-3 py-1.5 ${cat.className} border-0`}>
                <cat.icon className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />{cat.label}
              </Badge>
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} className={`w-20 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-colors ${imgIdx === i ? 'border-primary' : 'border-transparent'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Details */}
            <div className="mt-6 space-y-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#2C362F] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="listing-title">
                  {listing.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  {listing.location && (
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" strokeWidth={1.5} />{listing.location}</span>
                  )}
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" strokeWidth={1.5} />{timeAgo(listing.created_at)}</span>
                  <span className="flex items-center gap-1"><Eye className="w-4 h-4" strokeWidth={1.5} />{listing.views} views</span>
                </div>
              </div>

              {listing.category === 'sale' && listing.price != null && (
                <div className="bg-white border border-[#E8E6DF] rounded-2xl p-4">
                  <span className="text-3xl font-bold text-primary" data-testid="listing-price">£{listing.price.toFixed(2)}</span>
                </div>
              )}

              <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-[#2C362F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Description</h2>
                <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap" data-testid="listing-description">{listing.description}</p>
              </div>

              {listing.category === 'lending' && (
                <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-[#2C362F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Lending Details</h2>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {listing.lending_duration && <div><span className="text-muted-foreground">Duration:</span><p className="font-medium">{listing.lending_duration}</p></div>}
                    {listing.lending_deposit != null && <div><span className="text-muted-foreground">Deposit:</span><p className="font-medium">£{listing.lending_deposit.toFixed(2)}</p></div>}
                    {listing.lending_terms && <div className="col-span-2"><span className="text-muted-foreground">Terms:</span><p className="font-medium">{listing.lending_terms}</p></div>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Seller + Contact */}
          <div className="space-y-6">
            <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6 sticky top-20" data-testid="seller-info-card">
              {listing.seller && (
                <Link to={`/profile/${listing.seller.user_id}`} className="flex items-center gap-3 mb-4 group">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                    {listing.seller.picture ? (
                      <img src={listing.seller.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C362F] group-hover:text-primary transition-colors">{listing.seller.name || 'User'}</p>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      {listing.seller.rating_count > 0 && (
                        <>
                          <Star className="w-3.5 h-3.5 text-[#E5B66A] fill-[#E5B66A]" />
                          <span>{listing.seller.rating_avg}</span>
                          <span>({listing.seller.rating_count})</span>
                        </>
                      )}
                      {listing.seller.location && <span className="ml-1">{listing.seller.location}</span>}
                    </div>
                  </div>
                </Link>
              )}

              {user && user.user_id && user.user_id !== listing.user_id && (
                <div className="space-y-3">
                  <Textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder={`Hi, I'm interested in "${listing.title}"...`}
                    className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] min-h-[100px] resize-none"
                    data-testid="contact-message-input"
                  />
                  <Button
                    className="w-full rounded-xl h-11 bg-primary hover:bg-primary/90 text-white gap-2"
                    onClick={handleContact}
                    disabled={sending}
                    data-testid="send-message-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" strokeWidth={1.5} />}
                    Send Message
                  </Button>
                  <Button variant="ghost" size="sm" className="w-full rounded-xl text-muted-foreground gap-1.5" onClick={handleReport} data-testid="report-listing-btn">
                    <Flag className="w-3.5 h-3.5" strokeWidth={1.5} />Report Listing
                  </Button>
                </div>
              )}

              {(!user || !user.user_id) && (
                <Link to="/login">
                  <Button className="w-full rounded-xl h-11 bg-primary text-white" data-testid="login-to-contact-btn">
                    Sign in to Contact Seller
                  </Button>
                </Link>
              )}

              {user && user.user_id === listing.user_id && (
                <p className="text-sm text-muted-foreground text-center">This is your listing</p>
              )}
            </div>

            <AdBanner placement="listing" />
          </div>
        </div>
      </div>
    </div>
  );
}
